/**
 * 트랜잭션 / 커넥션 관리 유틸
 *
 * @example
 * // 트랜잭션 (에러 시 자동 롤백)
 * await withTransaction(async (conn) => {
 *   const id = await insert("orders", { userId: 1 }, conn);
 *   await update("users", { balance: 0 }, { id: 1 }, conn);
 * });
 *
 * @example
 * // 단일 커넥션 (트랜잭션 없이 커넥션 재사용)
 * await withConnection(async (conn) => {
 *   const user = await queryOne<User>("SELECT ...", [], conn);
 *   const logs = await queryAll("SELECT ...", [], conn);
 * });
 */

import { getPool, type DbConnection } from "./configure";

/** 트랜잭션 — 에러 시 자동 롤백 후 예외를 다시 던진다. */
export const withTransaction = async (
    callback: (conn: DbConnection) => Promise<void>
): Promise<void> => {
    const conn = await getPool().getConnection();
    try {
        await conn.beginTransaction();
        await callback(conn);
        await conn.commit();
    } catch (err) {
        await conn.rollback();
        throw err;
    } finally {
        conn.release();
    }
};

/** 단일 커넥션으로 여러 쿼리 처리 (트랜잭션 없음, 커넥션 자동 반환). */
export const withConnection = async (
    callback: (conn: DbConnection) => Promise<void>
): Promise<void> => {
    const conn = await getPool().getConnection();
    try {
        await callback(conn);
    } finally {
        conn.release();
    }
};
